import React from 'react';
import { Link } from 'react-router-dom';
import { SEO, Section } from '../components/Shared';
import { contentRegistry } from '../contentRegistry';
import { getArcticCoachUrl } from '../constants/config';

const Home: React.FC = () => {
  const featuredItems = Object.values(contentRegistry).filter(item => item.featured);
  
  return (
    <div className="relative overflow-hidden arctic-bg">
      <SEO title="Lapland AI Lab | Tietoturvaopas" description="Arktinen tietoturvaportaali vNext." />
      
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-500/10 rounded-full blur-[140px] -z-10 animate-pulse-soft"></div>
      <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[160px] -z-10 animate-pulse-soft"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center mb-40 relative">
          <div className="inline-block px-5 py-2 mb-8 rounded-full border border-teal-400/30 bg-teal-400/10 text-teal-200 text-xs font-black uppercase tracking-[0.3em] animate-in fade-in slide-in-from-top duration-700">
            Arctic Security vNext / certification ready
          </div>
          <h1 className="text-7xl md:text-9xl font-black text-white mb-10 tracking-tighter leading-[0.8]">
            Arctic <br/><span className="aurora-text">Resilienssi</span>
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-20">
            <Link to="/content/kyber-perusteet" className="arctic-card p-8 rounded-[2rem] hover:border-teal-300/40 transition-all group">
              <span className="text-teal-300 text-[10px] font-black uppercase tracking-widest block mb-4">Polku 1</span>
              <h3 className="text-xl font-black text-white mb-2">Peruskäyttäjä</h3>
              <p className="text-slate-500 text-xs uppercase tracking-widest">Kyber → Mobiili → Rutiinit</p>
            </Link>
            <Link to="/content/ai-safety" className="arctic-card p-8 rounded-[2rem] hover:border-purple-300/40 transition-all group">
              <span className="text-purple-300 text-[10px] font-black uppercase tracking-widest block mb-4">Polku 2</span>
              <h3 className="text-xl font-black text-white mb-2">AI-Käyttäjä</h3>
              <p className="text-slate-500 text-xs uppercase tracking-widest">AI-Turva → Tietosuoja → Trendit</p>
            </Link>
            <Link to="/business-audit" className="arctic-card p-8 rounded-[2rem] hover:border-sky-300/40 transition-all group">
              <span className="text-sky-300 text-[10px] font-black uppercase tracking-widest block mb-4">Polku 3</span>
              <h3 className="text-xl font-black text-white mb-2">Johto</h3>
              <p className="text-slate-500 text-xs uppercase tracking-widest">Auditointi → Kriisi → Trendit</p>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-40">
          {featuredItems.map((item) => (
            <Link
              key={item.slug}
              to={`/content/${item.slug}`}
              className="group arctic-card p-10 rounded-[3.5rem] hover:border-teal-300/50 transition-all duration-700 transform hover:-translate-y-4 flex flex-col h-full relative overflow-hidden"
            >
              <h2 className="text-3xl font-black text-white mb-6 group-hover:text-teal-300 transition-colors tracking-tight uppercase leading-none">
                {item.navLabel}
              </h2>
              <p className="text-slate-400 mb-12 leading-relaxed text-base flex-grow font-medium">
                {item.hero.subtitle}
              </p>
              <div className="flex items-center text-teal-300 font-black text-[10px] tracking-[0.25em] uppercase">
                Syvenny & Testaa
              </div>
            </Link>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mb-32">
          <div className="arctic-panel rounded-[3rem] p-12 md:p-14 border border-white/10 relative overflow-hidden">
            <div className="absolute -top-20 -right-16 w-40 h-40 bg-teal-400/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-16 w-40 h-40 bg-purple-400/10 rounded-full blur-3xl"></div>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10">
              <div>
                <p className="text-teal-300 text-xs font-black uppercase tracking-[0.25em] mb-4">Suunnitelma</p>
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Tiekartta kohti arktista resilienssiä</h2>
                <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
                  Selkeä eteneminen auttaa pitämään kokonaisuuden rauhallisena ja portfolio-first -lähestymisessä.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-1">
                {['Tavoite', 'Rajaus', 'Toteutus', 'Julkaisu'].map((step, index) => (
                  <div key={step} className="arctic-card rounded-3xl p-6 border border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-black mb-3">Vaihe {index + 1}</div>
                    <h3 className="text-xl font-black text-white mb-2">{step}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      {step === 'Tavoite' && 'Määritä kirkas lopputulos ja vaikuttavuus.'}
                      {step === 'Rajaus' && 'Rajaa sisältö ydinarvoihin ja käyttäjäpolkuun.'}
                      {step === 'Toteutus' && 'Rakenna arctic-panelit ja testaa sisältö.'}
                      {step === 'Julkaisu' && 'Viimeistele CTA:t ja julkaise hallitusti.'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/business-audit" className="btn-primary px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm text-center">
                Aloita suunnitelma
              </Link>
              <Link to="/content/kyber-perusteet" className="btn-ghost px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-sm text-center">
                Kysy lisää
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-12">
          {/* Arctic Coach CTA Section */}
          <div className="arctic-panel p-12 rounded-[3.5rem] border-teal-400/20 shadow-[0_0_80px_rgba(45,212,191,0.12)] text-center relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-teal-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl"></div>
            
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-400/20 text-teal-200 text-[10px] font-black uppercase tracking-widest mb-6">
              AI-Koulutusvalmentaja
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight uppercase">Kysy Arctic Coachilta</h2>
            <p className="text-slate-400 mb-10 max-w-lg mx-auto font-medium text-lg">
              Koulutus- ja tenttivalmentaja (ei reaaliaikaista valvontaa).
            </p>
            <a 
              href={getArcticCoachUrl('home')} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block btn-primary px-12 py-6 rounded-3xl font-black uppercase tracking-widest transition-all transform hover:scale-105"
            >
              Avaa Arctic Coach
            </a>
          </div>

          <Section 
            title="Sertifioidu Asiantuntijaksi"
            checklist={[
              "Käy läpi kaikki 12 moduulia",
              "Auditoi omat rutiinisi",
              "Läpäise sertifiointitesti"
            ]}
            colorClass="bg-indigo-600"
            slug="home-certification"
          >
            Haluatko osoittaa osaamisesi? Suorita kaikki osa-alueet ja osallistu loppukokeeseen. Arctic Security Certification on merkki vahvasta tietoturvaosaamisesta.
            <div className="mt-10">
              <Link to="/quiz/sertifikaatti" className="btn-primary px-10 py-5 rounded-2xl font-black uppercase tracking-widest transition-all inline-block">
                Aloita loppukoe
              </Link>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Home;
